// Function to calculate the new size based on the 4096 max dimension
function calculateSize(file: File, maxDimension: number): Promise<[number, number]> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();

        reader.onload = (event) => {
            const result = event.target.result as string;

            img.onload = () => {
                let width = img.width;
                let height = img.height;
                let newWidth, newHeight;

                // Calculate the new dimensions based on the maxDimension limit
                if (width >= maxDimension || height >= maxDimension) {
                    if (width > height) {
                        newHeight = Math.round((height * maxDimension) / width);
                        newWidth = maxDimension;
                    } else if (height > width) {
                        newWidth = Math.round((width * maxDimension) / height);
                        newHeight = maxDimension;
                    } else {
                        // Case for square images (width == height)
                        newWidth = Math.round((width * maxDimension) / height);
                        newHeight = Math.round((height * maxDimension) / width);
                    }
                }
                resolve([newWidth, newHeight]);
            };

            img.src = result;
        };

        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
    });
}

// Function to resize the image and reduce its file size
function jpegCompressImage(file: File, newWidth: number, newHeight: number, quality = 0.9): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);

        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");

                canvas.width = newWidth;
                canvas.height = newHeight;

                ctx.drawImage(img, 0, 0, newWidth, newHeight);

                canvas.toBlob((blob) => {
                    // clean the canvas
                    // canvas.remove();

                    if (blob) resolve(blob);
                    else reject(new Error("Blob creation failed"));
                },
                "image/jpeg",
                quality
                );
            };
            const result = event.target.result as string;
            img.src = result;
        };
        reader.onerror = (error) => reject(error);
    });
}

// Main function to resize and compress image based on conditions
export async function resizeAndCompressImage(asset: File): Promise<File> {
    try {
        // First, create an Image object to get the actual height and width of the file
        const img = new Image();
        const reader = new FileReader();
        
        return new Promise((resolve, reject) => {
            reader.onload = async (event) => {
                const result = event.target.result as string;
                img.onload = async () => {
                    const originalWidth = img.width;
                    const originalHeight = img.height;

                    // Calculate the new size based on max dimension
                    const [newWidth, newHeight] = await calculateSize(asset, 4096);

                    // Define the size limit (1.5MB)
                    const sizeLimit = 1.5 * 1024 * 1024; // 1.5MB in bytes

                    // Check the file size and resolution
                    if (asset.size > sizeLimit) {
                        // Case 1: Resolution > 4096px and Size > 1.5MB -> Resize and Compress
                        if (originalWidth > 4096 || originalHeight > 4096) {
                            console.log("Condition 1: Resize and compress (resolution > 4096px, size > 1.5MB)");

                            // Compress the image after resizing
                            const quality: number = +(process.env.NEXT_PUBLIC_JPEG_COMPRESSION_QUALITY || 0.9);
                            const compressedBlob = await jpegCompressImage(asset, newWidth, newHeight, quality);

                            // console.log(compressedBlob.size, newHeight, newWidth, 'taguuuu')

                            if (compressedBlob instanceof Blob) {
                                console.log("Resized and compressed blob size:", compressedBlob.size, newWidth, newHeight);
                                const compressedFile = new File([compressedBlob], asset.name, { type: "image/jpeg" });
                                resolve(compressedFile);
                            } else {
                                reject(new Error("Compression failed"));
                            }
                        } 
                        // Case 2: Resolution <= 4096px and Size > 1.5MB -> Only compress
                        else {
                            console.log("Condition 2: Only compress (resolution <= 4096px, size > 1.5MB)");

                            // Compress without resizing
                            const quality: number = +(process.env.NEXT_PUBLIC_JPEG_COMPRESSION_QUALITY || 0.9);
                            const compressedBlob = await jpegCompressImage(asset, originalWidth, originalHeight, quality);

                            if (compressedBlob instanceof Blob) {
                                console.log("Compressed blob size:", compressedBlob.size);
                                const compressedFile = new File([compressedBlob], asset.name, { type: "image/jpeg" });
                                resolve(compressedFile);
                            } else {
                                reject(new Error("Compression failed"));
                            }
                        }
                    } 
                    // Case 3: Resolution > 4096px and Size <= 1.5MB -> Resize and compress if resized size is larger
                    else {
                        // This is the new case for square images
                        if (originalWidth > 4096 || originalHeight > 4096) {
                            console.log("Condition 3: Resize image if resolution > 4096px and size <= 1.5MB");

                            // Resize the square image
                            const resizedBlob = await jpegCompressImage(asset, newWidth, newHeight, 1); // Resize with no compression
                            if (resizedBlob.size > asset.size) {
                                console.log("Resized size is greater than original size, compressing.");
                                const quality: number = +(process.env.NEXT_PUBLIC_JPEG_COMPRESSION_QUALITY || 0.9);
                                const compressedBlob = await jpegCompressImage(asset, newWidth, newHeight, quality);

                                if (compressedBlob instanceof Blob) {
                                    const resizedFile = new File([compressedBlob], asset.name, { type: "image/jpeg" });
                                    resolve(resizedFile);
                                } else {
                                    reject(new Error("Compression failed after resizing"));
                                }
                            } else {
                                console.log("Resized image is smaller, returning resized image");
                                const resizedFile = new File([resizedBlob], asset.name, { type: "image/jpeg" });
                                resolve(resizedFile);
                            }
                        } else {
                            console.log("Condition 4: No resizing or compression needed (resolution <= 4096px, size <= 1.5MB)");
                            resolve(asset); // Return the original file if no operation is required
                        }
                    }
                };
                img.src = result;
            };
            reader.readAsDataURL(asset);
        });
    } catch (error) {
        throw new Error("Error processing image: " + error.message);
    }
}
